import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@epoch-ui/components/Button';
import { Form, FormFieldSet, CheckboxField } from '@epoch-ui/components/Form';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@epoch-ui/components/Modal/modal';
import { RadioField, RadioFieldItem } from '@epoch-ui/components/Form/fields/RadioField/RadioField';
import { FeedbackSchedule } from 'types/questions.types';
import { EditableEvent } from 'types/event.types';
import { getPendingScheduledPublishAt } from '@event-editor/utils';
import { cn } from '@epoch-ui/lib/utils';
import dayjs from 'utils/extendedDayJS';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useEditFeedbackSettingsModal } from '@event-editor/hooks/eventEditorState';
import {
  updateEventFeedbackSettings,
  UpdateEventFeedbackSettingsInput
} from '@event-editor/api/feedback/updateEventFeedbackSettings';

import { FeedbackScheduleSelector } from './FeedbackScheduleSelector';
import {
  getFeedbackScheduleSchema,
  FeedbackSettingsFormValues,
  FeedbackSettingsFormInput,
  FEEDBACK_SETTINGS_DEFAULTS,
  FEEDBACK_ANONYMITY,
  convertDBToFormData
} from './feedbackSettingsSchema';

const EditFeedbackSettingsModal: React.FC = () => {
  const event = useEditableEvent();
  const queryClient = useQueryClient();
  const { isOpen, open, close } = useEditFeedbackSettingsModal();

  const updateSettingsMutation = useMutation({
    mutationFn: (variables: UpdateEventFeedbackSettingsInput) =>
      updateEventFeedbackSettings(variables),
    onSuccess: (result, variables) => {
      // Update the event query data in the cache
      queryClient.setQueryData(
        eventEditorQueryKeys.event(variables.eventId),
        (oldData: { event: EditableEvent } | undefined) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  isFeedbackAnonymous: result.updateEventFeedbackSettings.event.isFeedbackAnonymous,
                  feedbackSchedule: result.updateEventFeedbackSettings.event.feedbackSchedule,
                  feedbackNotificationChannel:
                    result.updateEventFeedbackSettings.event.feedbackNotificationChannel
                }
              }
            : oldData
      );

      // Invalidate communications queries
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventCommunication(event.id!)
      });

      // Close the modal
      close();
    }
  });

  const showSendAtByDefault = useMemo(
    () =>
      !event.feedbackSchedule?.sendAt &&
      (event.postQuestions?.filter((q) => !q.isDeleted) || []).length > 0,
    [event.postQuestions, event.feedbackSchedule?.sendAt]
  );

  // Get initial values by merging defaults with event data
  const initialValues = useMemo<FeedbackSettingsFormInput>((): FeedbackSettingsFormInput => {
    let eventFeedbackSchedule: FeedbackSchedule = {};

    if (
      event.feedbackSchedule &&
      event.feedbackSchedule.quantity &&
      event.feedbackSchedule.unit &&
      event.feedbackSchedule.beforeEventEnds !== undefined
    ) {
      // Relative time is set
      eventFeedbackSchedule = {
        ...event.feedbackSchedule,
        sendAt: undefined
      };
    } else if (event.feedbackSchedule?.sendAt) {
      // Set absolute time
      eventFeedbackSchedule.sendAt = event.feedbackSchedule.sendAt;
    } else if (!showSendAtByDefault) {
      // No time is set, set default values
      eventFeedbackSchedule = { ...FEEDBACK_SETTINGS_DEFAULTS.feedbackSchedule };
    } else {
      // Show send at by default
      eventFeedbackSchedule = {};
    }

    const dbValues = {
      isFeedbackAnonymous:
        event.isFeedbackAnonymous ?? FEEDBACK_SETTINGS_DEFAULTS.isFeedbackAnonymous,
      feedbackSchedule: {
        ...eventFeedbackSchedule
      },
      notificationChannel: {
        ...FEEDBACK_SETTINGS_DEFAULTS.notificationChannel,
        ...event.feedbackNotificationChannel
      }
    };

    return convertDBToFormData(dbValues, event.timezoneCode);
  }, [event, showSendAtByDefault]);

  const disabled = useMemo(() => {
    if (!event.datetimeEnd) {
      return false;
    }

    const isFeedbackSent =
      event.isPublished && event.feedbackSchedule?.sendAt
        ? dayjs.utc().isAfter(dayjs.utc(event.feedbackSchedule.sendAt))
        : false;

    const thirtyDaysAfterEventEnd = dayjs
      .utc()
      .isAfter(dayjs.utc(event.datetimeEnd).add(30, 'days'));

    return isFeedbackSent || thirtyDaysAfterEventEnd;
  }, [event.datetimeEnd, event.isPublished, event.feedbackSchedule?.sendAt]);

  const pendingScheduledPublishAt = getPendingScheduledPublishAt(event.publishSchedule);

  // Initialize the form
  const methods = useForm<FeedbackSettingsFormInput, unknown, FeedbackSettingsFormValues>({
    resolver: zodResolver(
      getFeedbackScheduleSchema(
        event.datetimeScheduled ?? '',
        event.datetimeEnd ?? '',
        event.timezoneCode,
        pendingScheduledPublishAt
      )
    ),
    values: initialValues
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  return (
    <Modal open={isOpen} onOpenChange={(nextOpen) => (nextOpen ? open() : close())}>
      <ModalContent size="sm" className="tw-flex tw-flex-col" data-testid="feedback-settings-modal">
        <ModalHeader>
          <ModalTitle className="tw-text-dark-black">Feedback form settings</ModalTitle>
          <p className="tw-mb-1 tw-mt-2 tw-text-sm tw-text-primary-grey">
            Attended guests will receive a notification to submit their feedback via email and
            Slack.
          </p>
        </ModalHeader>

        <Form {...methods}>
          <form
            onSubmit={handleSubmit(
              (data: FeedbackSettingsFormValues) => {
                if (!event.id) return;

                updateSettingsMutation.mutate({
                  ...data,
                  eventId: event.id
                });
              },
              (errors) => {
                console.error(errors);
              }
            )}
            className={cn('tw-flex tw-flex-col', disabled && 'tw-opacity-40')}
          >
            <FormFieldSet className="tw-gap-4">
              {/* Feedback responses */}
              <RadioField
                name="isFeedbackAnonymous"
                label="Feedback responses are:"
                className="tw-mt-3"
                disabled={disabled}
              >
                <RadioFieldItem value={FEEDBACK_ANONYMITY.ANONYMOUS}>Anonymous</RadioFieldItem>
                <RadioFieldItem value={FEEDBACK_ANONYMITY.NOT_ANONYMOUS}>
                  Not anonymous
                </RadioFieldItem>
              </RadioField>

              {/* Send feedback form notification */}
              <div>
                <h3 className="tw-mb-0 tw-text-sm tw-font-medium">
                  Send the feedback form notification:
                </h3>
                <FeedbackScheduleSelector disabled={disabled} />
              </div>

              {/* Notification channels */}
              <div>
                <h3 className="tw-mb-0 tw-text-sm tw-font-medium">
                  How do you want to notify guests?
                </h3>
                <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
                  <CheckboxField
                    className="tw-mt-0"
                    name="notificationChannel.email"
                    label="Email"
                    disabled={disabled}
                  />
                  <CheckboxField
                    className="tw-mt-0"
                    name="notificationChannel.slack"
                    label="Slack"
                    disabled={disabled}
                  />
                </div>
              </div>

              <div className="tw-mt-1">
                <Button
                  size="sm"
                  className="tw-w-full"
                  type="submit"
                  disabled={isSubmitting || updateSettingsMutation.isPending || disabled}
                >
                  Save
                </Button>
              </div>
            </FormFieldSet>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export { EditFeedbackSettingsModal };
